import React from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { PopoverMenuItem, Text, currency, useModalControl } from '@frontend/design-system';
import { useCheckCanSendMessages } from '../../hooks/use-check-can-send-messages';
import {
  type RequestSource,
  useScheduleRequestItemHeaderController,
} from '../../hooks/use-schedule-request-item-header-controller';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import {
  type ScheduleListItemVariant,
  ScheduleRequestItemHeaderUI,
} from '../components/schedule-request-item-header-ui';

interface Props {
  requestVersion: string;
  name: string;
  appointmentTime: string;
  personPhone: string;
  paidAmount: number;
  locationId: string;
  scheduleRequestId: string;
  variant?: ScheduleListItemVariant;
  locationChip?: (locationName: string) => React.ReactNode;
  updateSelectedScheduleRequest: () => void;
  deleteRequestModalProps: ReturnType<typeof useModalControl>;
  rejectRequestModalProps?: ReturnType<typeof useModalControl>;
  onMessage: (updateStatus: boolean) => void;
  onPhoneCall?: () => void;
  requestSource: RequestSource;
  moreInfo?: React.ReactNode;
}

export const ScheduleRequestListItemHeader = React.memo(
  ({
    requestVersion,
    name,
    paidAmount,
    appointmentTime,
    personPhone,
    locationId,
    scheduleRequestId,
    variant,
    updateSelectedScheduleRequest,
    locationChip,
    deleteRequestModalProps,
    rejectRequestModalProps,
    onMessage,
    onPhoneCall,
    requestSource,
    moreInfo,
  }: Props) => {
    const { t } = useTranslation('schedule-request');
    const canSendMessages = useCheckCanSendMessages(locationId);

    const {
      shouldUseV3,
      locationName,
      appointmentTimeLabel,
      getMenuProps,
      getTriggerProps,
      handleReviewRequestMenuItemClick,
      handleRejectRequestMenuItemClick,
      handleDeleteMenuItemClick,
      handleMessageMenuItemClick,
    } = useScheduleRequestItemHeaderController({
      requestVersion,
      locationId,
      scheduleRequestId,
      appointmentTime,
      variant,
      deleteRequestModalProps,
      rejectRequestModalProps,
      onMessage,
      onPhoneCall,
      requestSource,
    });

    const handleUpdateSelectedScheduleRequest = () => handleReviewRequestMenuItemClick(updateSelectedScheduleRequest);

    const secondaryMeta = (
      <div css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)' }}>
        {!!paidAmount ? (
          <>
            <Text textAlign='left' size='small' css={{ display: 'inline', color: theme.colors.tangerine50 }}>
              {currency(paidAmount, { decimals: 2 })}
            </Text>
            {` • `}
          </>
        ) : null}
        <Text textAlign='left' size='small'>
          {appointmentTimeLabel}
        </Text>
      </div>
    );

    const menuItems = (
      <>
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemReviewRequestBtn}
          Icon={() => <Icon name='lists-small' />}
          onClick={handleUpdateSelectedScheduleRequest}
        >
          {t('Review Request')}
        </PopoverMenuItem>
        {personPhone && (
          <PopoverMenuItem
            trackingId={ScheduleRequestsTrackingIds.requestListItemMessageBtn}
            Icon={() => <Icon name='message-small' />}
            disabled={!canSendMessages}
            hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
            onClick={handleMessageMenuItemClick}
          >
            {t('Message')}
          </PopoverMenuItem>
        )}
        {shouldUseV3 && (
          <PopoverMenuItem
            trackingId={ScheduleRequestsTrackingIds.requestListItemRejectBtn}
            Icon={() => <Icon name='calendar-discarded-small' />}
            onClick={handleRejectRequestMenuItemClick}
          >
            {t('Reject Request')}
          </PopoverMenuItem>
        )}
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDeleteBtn}
          Icon={() => <Icon name='trash-small' />}
          onClick={handleDeleteMenuItemClick}
        >
          {t('Delete')}
        </PopoverMenuItem>
      </>
    );

    return (
      <ScheduleRequestItemHeaderUI
        name={name}
        locationName={locationName}
        secondaryMeta={secondaryMeta}
        moreInfo={moreInfo}
        menuItems={menuItems}
        locationChip={locationChip}
        getTriggerProps={getTriggerProps}
        getMenuProps={getMenuProps}
        variant={variant}
        onClick={variant === 'overlappingPanel' ? handleUpdateSelectedScheduleRequest : undefined}
      />
    );
  }
);

ScheduleRequestListItemHeader.displayName = 'ScheduleRequestListItemHeader';
